import { ISocketClient, SOCKET_CONNECTION_STATE } from '@base/features/socket/interfaces';
import { SocketCodes } from '@base/utils/constants';
import { getEnv, getRequiredEnv } from '@base/utils/helpers';
import { Subject } from 'rxjs';
import { Session } from '../user/model/interfaces';
import BufferReader from './bufferReader';
import Codes from './codes';
import DataStream from './dataStream';
import Utils from './utils';

const EMPTY_CONNECTION: Session = { host: '', port: 0, proxy: [], usr: '', session: '' };

export default class SocketClient implements ISocketClient {
  private subscribers: Record<string, any>[] = [];
  private socket?: WebSocket;
  private input = new DataStream((data: BufferReader) => this.readPacket(data));
  private pingSocketInterval: NodeJS.Timeout | null = null;
  private PING_INTERVAL_MS = 2000;
  private DEFAULT_CONNECTION: Session = EMPTY_CONNECTION;
  private CURRENT_CONNECTION: Session = EMPTY_CONNECTION;
  private MAX_RECONNECTS = 10;
  private reconnectsCounter = 0;
  onConnectionError?: () => void;
  onConnect = new Subject<Session>();

  connectDefault(): void {
    console.log(this.DEFAULT_CONNECTION);
    this.connect(this.DEFAULT_CONNECTION);
  }

  login(usr: string, session: string) {
    const data = [SocketCodes.CP_LOGIN, 0]
      .concat(Utils.UTFtoArray(usr, true, 2))
      .concat(Utils.UTFtoArray(session, true, 2));
    this.sendPacket(data);
  }

  sendPacket(dataArray: any[]): void {
    if (this.socket?.readyState === SOCKET_CONNECTION_STATE.OPEN) {
      dataArray.unshift(0);
      const data = new Uint8Array(dataArray).buffer;
      this.socket.send(data);
    }
  }

  connect(config: Session): string {
    // Если есть открытое соединение
    if (this.socket) {
      this.stopPing();
      this.socket.onclose = null;
      this.socket.close();
    }

    // Берем один прокси из списка
    let proxy;
    if (Array.isArray(config.proxy)) {
      proxy = config.proxy.shift();
    } else {
      proxy = config.proxy;
      config.proxy = '';
    }

    // Если прокси в списке закончились
    if (!proxy) {
      this.onConnectionError?.();
      throw new Error('Could not connect to any of the proxies. Aborting.');
    }

    // Пробуем присоединиться
    this.socket = new WebSocket(proxy.startsWith('wss://') ? proxy : `wss://${proxy}`);
    this.socket.binaryType = 'arraybuffer';

    // Если не получилось открыть прокси соединение, пробуем оставшиеся прокси в списке
    this.socket.onerror = () => this.connect(config);

    // Если получилось открыть прокси соединение
    this.socket.onopen = () => {
      if (this.socket) {
        this.socket.onmessage = (e: MessageEvent) => this.input.push(e.data);
        this.socket.onclose = this.networkClosed.bind(this);
        this.socket.onerror = this.networkError.bind(this);
        console.log('Socket connection opened');
        this.reconnectsCounter = 0;
        // После создания сокет соединения, надо подключиться (залогиниться)
        // Для этого отправляется хост и порт, а затем логин
        this.socket.send(JSON.stringify({ host: config.host, port: config.port }));
        this.login(config.usr, config.session);
        this.CURRENT_CONNECTION = { ...config };
        this.startPing();
        this.onConnect.next(this.CURRENT_CONNECTION);
        this.sendClientVersion();
      }
    };
    return proxy;
  }

  private sendClientVersion() {
    // const data = [SocketCodes.CP_CLIENT_VERSION]
    // .concat(Utils.UTFtoArray('v1.0.0', true, 2))
    // this.sendPacket(data);
  }

  private readPacket(packet: BufferReader) {
    /* const packetNum = */
    packet.readUint8();
    const type = packet.readUint8();
    const typeName: string = (Codes.Types.names.receiving as any)[type];

    if (!typeName) {
      console.info('Received packet of unknown type:', type);
      return;
    }

    // Transform event name to method name like SP_YOU_ARE -> onReceiveYouAre
    for (const subscriber of this.subscribers) {
      const name = Utils.toCamelCase(typeName.replace('SP', 'ON_RECEIVE'));
      if (typeof subscriber[name] === 'function') {
        return subscriber[name](packet);
      }
    }
    console.error(`No method for socket event ${name} found`);
  }

  private sendPing(): void {
    const data = [SocketCodes.CP_PING];
    this.sendPacket(data);
  }

  private startPing(): void {
    this.pingSocketInterval = setInterval(() => {
      this.sendPing();
    }, this.PING_INTERVAL_MS);
  }

  private networkError(e: any): void {
    console.error(`Socket: network error: `, e);
    this.stopPing();
    this.reconnect();
    this.DEFAULT_CONNECTION = EMPTY_CONNECTION;
  }

  private stopPing(): void {
    if (!this.pingSocketInterval) return;
    clearInterval(this.pingSocketInterval);
    this.pingSocketInterval = null;
  }

  private reconnect(): void {
    if (this.reconnectsCounter < this.MAX_RECONNECTS) {
      console.info('Trying to reconnect.');
      this.connect(this.CURRENT_CONNECTION);
      this.reconnectsCounter++;
    }
  }

  private networkClosed(e: CloseEvent): void {
    this.stopPing();
    console.trace('%cSocket connection closed: ', 'color:blue', e);
    if (e.code === 1006) {
      this.reconnect();
    }
  }

  setDefaultConnection(config: Session) {
    this.DEFAULT_CONNECTION = config;
  }

  closeConnection(): void {
    this.stopPing();
    this.sendPacket([Codes.Types.CP_QUIT]);
    this.socket?.close(1000);
  }

  isConnected(): boolean {
    return Boolean(this.socket) && this.socket?.readyState === SOCKET_CONNECTION_STATE.OPEN;
  }

  isConnectedToDefault() {
    return this.isConnectedTo(this.DEFAULT_CONNECTION.host, this.DEFAULT_CONNECTION.port);
  }

  isConnectedTo(host: string, port: number): boolean {
    return host === this.CURRENT_CONNECTION.host && port === this.CURRENT_CONNECTION.port;
  }

  subscirbe(...args: any) {
    this.subscribers.push(...args);
  }
}
